import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Lottie from 'lottie-react'
import correctAnim from '../../../assets/lottie/quiz-result/correct.json'
import correctComboAnim from '../../../assets/lottie/quiz-result/correct_combo.json'
import wrongAnim from '../../../assets/lottie/quiz-result/wrong.json'
import wrongComboAnim from '../../../assets/lottie/quiz-result/wrong_combo.json'
import correctMp3 from '../../../assets/sounds/correct.mp3'
import correctComboMp3 from '../../../assets/sounds/correct_combo.mp3'
import wrongMp3 from '../../../assets/sounds/wrong.mp3'
import wrongComboMp3 from '../../../assets/sounds/wrong_combo.mp3'

interface QuizResultPopupProps {
  isOpen: boolean
  isCorrect: boolean
  isCombo: boolean
  onClose: () => void
  /** Skip the sound effect. */
  disableSound?: boolean
}

function pickAnim(isCorrect: boolean, isCombo: boolean) {
  if (isCorrect) return isCombo ? correctComboAnim : correctAnim
  return isCombo ? wrongComboAnim : wrongAnim
}

function pickSound(isCorrect: boolean, isCombo: boolean): string {
  if (isCorrect) return isCombo ? correctComboMp3 : correctMp3
  return isCombo ? wrongComboMp3 : wrongMp3
}

function pickAriaKey(isCorrect: boolean, isCombo: boolean): string {
  if (isCorrect) return isCombo ? 'result.popup.correctComboAriaLabel' : 'result.popup.correctAriaLabel'
  return isCombo ? 'result.popup.wrongComboAriaLabel' : 'result.popup.wrongAriaLabel'
}

export function QuizResultPopup({
  isOpen,
  isCorrect,
  isCombo,
  onClose,
  disableSound = false,
}: QuizResultPopupProps) {
  const { t } = useTranslation('quiz')

  useEffect(() => {
    if (!isOpen) return
    const timeoutMs = isCombo ? 4000 : 3000
    const timer = setTimeout(onClose, timeoutMs)
    return () => clearTimeout(timer)
  }, [isOpen, isCombo, onClose])

  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen || disableSound) return
    try {
      const audio = new Audio(pickSound(isCorrect, isCombo))
      audio.volume = 0.5
      audio.play().catch(() => {
        /* autoplay policy: silent fail */
      })
    } catch {
      /* environment without Audio: silent fail */
    }
  }, [isOpen, isCorrect, isCombo, disableSound])

  if (!isOpen) return null

  return (
    <div
      role="status"
      aria-live="assertive"
      aria-label={t(pickAriaKey(isCorrect, isCombo))}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center cursor-pointer"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative">
        <Lottie
          data-testid="quiz-result-popup-lottie"
          animationData={pickAnim(isCorrect, isCombo)}
          loop={false}
          autoplay={true}
          style={{ width: 360, height: 360 }}
        />
      </div>
    </div>
  )
}
